import { Target, ShieldAlert, TrendingUp, AlertTriangle, BookOpen, Eye, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DeepAnalysis } from '@/hooks/useDeepAnalysis';

interface SwingTradeSetupProps {
  analysis: DeepAnalysis;
}

export function SwingTradeSetup({ analysis }: SwingTradeSetupProps) {
  const { swingTradeSetup, technicalPicture, positionSizing, keyRisks, whatToWatch, educationalNote, riskAssessment, tradingSetup } = analysis;
  const historicalSupports = analysis.historicalIndicators?.support_levels || [];
  const historicalResistances = analysis.historicalIndicators?.resistance_levels || [];
  const indicatorCurrency = analysis.historicalAvailability?.currency || analysis.historicalIndicators?.currency || 'usd';
  const hasAth = Boolean(analysis.historicalIndicators?.ath_price_usd || analysis.marketData.ath);
  const watchItems = Array.isArray(whatToWatch) ? whatToWatch : [];
  const visibleWhatToWatch = hasAth ? watchItems : watchItems.filter((item) => !/\bATH\b|all[- ]time high|from ATH|ath_price/i.test(item));

  const formatPrice = (price: number) => {
    if (!price) return 'A$0';
    if (price < 0.01) return `A$${price.toFixed(6)}`;
    if (price < 1) return `A$${price.toFixed(4)}`;
    if (price < 100) return `A$${price.toFixed(2)}`;
    return `A$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

  const formatUsd = (price: number) => {
    if (!price) return 'US$0';
    if (price < 0.01) return `US$${price.toFixed(6)}`;
    if (price < 1) return `US$${price.toFixed(4)}`;
    return `US$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

  const formatIndicatorPrice = (price: number | null | undefined) => price ? (indicatorCurrency === 'aud' ? formatPrice(price) : formatUsd(price)) : `${indicatorCurrency === 'aud' ? 'A$' : 'US$'}—`;

  const getRiskColor = (level: string) => {
    switch (level?.toLowerCase()) {
      case 'low': return 'bg-success text-success-foreground';
      case 'medium': return 'bg-warning text-warning-foreground';
      case 'high': return 'bg-destructive/80 text-destructive-foreground';
      case 'very high': return 'bg-destructive text-destructive-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'High': return 'border-destructive text-destructive';
      case 'Medium': return 'border-warning text-warning';
      case 'Low': return 'border-muted-foreground text-muted-foreground';
      default: return 'border-muted text-muted-foreground';
    }
  };

  // Use enhanced swing trade setup if available, otherwise fall back to basic tradingSetup
  const hasEnhancedSetup = swingTradeSetup && swingTradeSetup.hasValidSetup !== undefined;

  return (
    <div className="space-y-6">
      {/* Technical Picture - Support/Resistance */}
      {technicalPicture && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Support Zones */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-success rotate-180" />
                <CardTitle className="text-base">Support Zones</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {(historicalSupports.length ? historicalSupports.map((price) => ({ price, explanation: `Support identified from ${indicatorCurrency.toUpperCase()} stored price structure` })) : technicalPicture.supportZones || []).map((zone, i) => (
                <div key={i} className="p-3 bg-success/10 rounded-lg border border-success/20">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-success">S{i + 1}</span>
                    <span className="font-bold text-foreground">{historicalSupports.length ? formatIndicatorPrice(zone.price) : formatPrice(zone.price)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{zone.explanation}</p>
                </div>
              ))}
              {historicalSupports.length === 0 && (!technicalPicture.supportZones || technicalPicture.supportZones.length === 0) && (
                <p className="text-sm text-muted-foreground">No clear structural levels identified in the current 90-day window</p>
              )}
            </CardContent>
          </Card>

          {/* Resistance Zones */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-destructive" />
                <CardTitle className="text-base">Resistance Zones</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {(historicalResistances.length ? historicalResistances.map((price) => ({ price, explanation: `Resistance identified from ${indicatorCurrency.toUpperCase()} stored price structure` })) : technicalPicture.resistanceZones || []).map((zone, i) => (
                <div key={i} className="p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-destructive">R{i + 1}</span>
                    <span className="font-bold text-foreground">{historicalResistances.length ? formatIndicatorPrice(zone.price) : formatPrice(zone.price)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{zone.explanation}</p>
                </div>
              ))}
              {historicalResistances.length === 0 && (!technicalPicture.resistanceZones || technicalPicture.resistanceZones.length === 0) && (
                <p className="text-sm text-muted-foreground">No clear structural levels identified in the current 90-day window</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Trend Info */}
      {technicalPicture && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div>
                <span className="text-xs text-muted-foreground">Trend</span>
                <p className="font-medium text-foreground">{technicalPicture.trend}</p>
              </div>
              <div className="flex-1">
                <span className="text-xs text-muted-foreground">Description</span>
                <p className="text-sm text-muted-foreground">{technicalPicture.trendDescription}</p>
              </div>
              {technicalPicture.chartPatterns && technicalPicture.chartPatterns.length > 0 && (
                <div>
                  <span className="text-xs text-muted-foreground">Patterns</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {technicalPicture.chartPatterns.map((pattern, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{pattern}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Swing Trade Setup */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-primary/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                <CardTitle className="text-base">Swing Trade Setup</CardTitle>
              </div>
              {hasEnhancedSetup && (
                <Badge variant={swingTradeSetup.hasValidSetup ? 'default' : 'secondary'}>
                  {swingTradeSetup.setupType?.replace('_', ' ') || 'N/A'}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {hasEnhancedSetup && swingTradeSetup.hasValidSetup ? (
              <>
                {/* Entry Zone */}
                <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-primary">Entry Zone</span>
                    <span className="font-bold text-foreground">
                      {formatPrice(swingTradeSetup.entryZone?.low || 0)} - {formatPrice(swingTradeSetup.entryZone?.high || 0)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{swingTradeSetup.entryZone?.explanation}</p>
                </div>

                {/* Take Profit Targets */}
                <div className="space-y-2">
                  <span className="text-sm font-medium text-muted-foreground">Take Profit Targets</span>
                  {(Array.isArray(swingTradeSetup.takeProfitTargets) ? swingTradeSetup.takeProfitTargets : []).map((target, i) => (
                    <div key={i} className="p-2 bg-success/10 rounded-lg border border-success/20 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-success" />
                        <span className="text-sm text-muted-foreground">{target.label}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-foreground">{formatPrice(target.price)}</span>
                        <span className="text-xs text-success ml-2">(+{target.percentGain?.toFixed(1) || 0}%)</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Stop Loss */}
                <div className="p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 text-destructive" />
                      <span className="text-sm font-medium text-destructive">Stop Loss</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-foreground">{formatPrice(swingTradeSetup.stopLoss?.price || 0)}</span>
                      <span className="text-xs text-destructive ml-2">(-{swingTradeSetup.stopLoss?.percentRisk?.toFixed(1) || 0}%)</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{swingTradeSetup.stopLoss?.explanation}</p>
                </div>

                {/* Risk/Reward */}
                <div className="flex items-center justify-between text-sm p-2 bg-muted/50 rounded">
                  <span className="text-muted-foreground">Risk/Reward Ratio</span>
                  <span className="font-medium text-foreground">{swingTradeSetup.riskRewardRatio}</span>
                </div>
              </>
            ) : hasEnhancedSetup && !swingTradeSetup.hasValidSetup ? (
              <div className="p-4 bg-warning/10 rounded-lg border border-warning/20 text-center">
                <AlertTriangle className="w-8 h-8 text-warning mx-auto mb-2" />
                <p className="text-sm font-medium text-warning">No Clear Setup</p>
                {swingTradeSetup.waitingFor && (
                  <p className="text-xs text-muted-foreground mt-2">
                    <strong>Waiting for:</strong> {swingTradeSetup.waitingFor}
                  </p>
                )}
              </div>
            ) : (
              // Fallback to basic trading setup
              <>
                <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-primary">Entry Zone</span>
                    <span className="font-bold text-foreground">{formatPrice(tradingSetup?.entry?.price || 0)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{tradingSetup?.entry?.zone}</p>
                </div>

                <div className="p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 text-destructive" />
                      <span className="text-sm font-medium text-destructive">Stop Loss</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-foreground">{formatPrice(tradingSetup?.stopLoss?.price || 0)}</span>
                      <span className="text-xs text-destructive ml-2">(-{tradingSetup?.stopLoss?.percentage?.toFixed(1) || 0}%)</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <span className="text-sm font-medium text-muted-foreground">Profit Targets</span>
                  {(tradingSetup?.targets || []).length > 0 ? tradingSetup?.targets?.map((target, i) => (
                    <div key={i} className="p-2 bg-success/10 rounded-lg border border-success/20 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-success" />
                        <span className="text-sm text-muted-foreground">Target {i + 1}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-foreground">{formatPrice(target.price)}</span>
                        <span className="text-xs text-success ml-2">(+{target.percentage?.toFixed(1)}%)</span>
                      </div>
                    </div>
                  )) : <p className="text-sm text-muted-foreground">No profit targets are available until a valid setup forms.</p>}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Position Sizing & Risk */}
        <div className="space-y-4">
          {/* Position Sizing */}
          {positionSizing && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-primary" />
                  <CardTitle className="text-base">Position Sizing</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Recommendation</span>
                  <Badge variant="outline">{positionSizing.recommendation}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Max Position</span>
                  <span className="font-medium text-foreground">{positionSizing.maxPositionPercent}% of portfolio</span>
                </div>
                <p className="text-xs text-muted-foreground">{positionSizing.explanation}</p>
                <div className="p-2 bg-muted/50 rounded text-xs text-muted-foreground">
                  <strong>Example:</strong> {positionSizing.dollarRiskExample}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Risk Assessment */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-warning" />
                  <CardTitle className="text-base">Risk Assessment</CardTitle>
                </div>
                <Badge className={getRiskColor(riskAssessment?.level || '')}>
                  {riskAssessment?.level || 'Unknown'} Risk
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Risk Score</span>
                <span className="font-bold text-foreground">{riskAssessment?.score || 0}/100</span>
              </div>

              {/* Key Risks with Severity */}
              {Array.isArray(keyRisks) && keyRisks.length > 0 ? (
                <div>
                  <span className="text-xs font-medium text-destructive">Key Risks</span>
                  <ul className="mt-2 space-y-2">
                    {keyRisks.map((risk, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Badge variant="outline" className={`text-[10px] ${getSeverityColor(risk.severity)}`}>
                          {risk.severity}
                        </Badge>
                        <span className="text-muted-foreground">{risk.risk}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : Array.isArray(riskAssessment?.factors) && (
                <div>
                  <span className="text-xs font-medium text-destructive">Risk Factors</span>
                  <ul className="mt-2 space-y-2">
                    {riskAssessment.factors.map((factor, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-destructive mt-1">⚠</span>
                        <span className="text-muted-foreground">{factor}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {Array.isArray(riskAssessment?.mitigations) && (
                <div className="pt-2 border-t border-border">
                  <span className="text-xs font-medium text-success">Risk Mitigations</span>
                  <ul className="mt-2 space-y-2">
                    {riskAssessment.mitigations.map((mitigation, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-success mt-1">✓</span>
                        <span className="text-muted-foreground">{mitigation}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* What to Watch */}
      {visibleWhatToWatch && visibleWhatToWatch.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-primary" />
              <CardTitle className="text-base">What to Watch</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {visibleWhatToWatch.map((item, i) => (
                <Badge key={i} variant="outline" className="py-1.5 px-3">
                  {item}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Educational Note */}
      {educationalNote && (
        <Card className="bg-primary/5 border-primary/20">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              <CardTitle className="text-base">Educational Note: {educationalNote.concept}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{educationalNote.explanation}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
